import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { formatCurrency, getStatusColor } from '../../utils/helper';

const StatusBadge = ({ status }) => {
  const normalizedStatus = status === 'compliant' ? 'ok' : status === 'at_risk' ? 'warning' : status === 'breach' ? 'critical' : status;
  const colors = getStatusColor(normalizedStatus);
  const icons = { ok: CheckCircle, warning: AlertTriangle, critical: XCircle, active: CheckCircle, archived: CheckCircle };
  const labels = { ok: 'Compliant', warning: 'At Risk', critical: 'Breach', active: 'Active', archived: 'Archived' };
  const Icon = icons[normalizedStatus] || CheckCircle;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${colors.bg} ${colors.text} ${colors.border}`}>
      <Icon size={14} />
      {labels[normalizedStatus] || status}
    </span>
  );
};

export default function LoanList({ loans, selectedLoanId, onSelectLoan }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      <div className="px-6 py-4 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-800">Active Loans</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Borrower
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Covenants
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loans.map((loan) => (
              <tr
                key={loan.id}
                onClick={() => onSelectLoan && onSelectLoan(loan)}
                className={`cursor-pointer transition-colors ${
                  selectedLoanId === loan.id
                    ? 'bg-blue-50 border-l-4 border-l-blue-500'
                    : 'hover:bg-slate-50'
                }`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {(loan.borrower || loan.borrower_name || loan.facility_name || 'L')?.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">{loan.borrower || loan.borrower_name || loan.facility_name || 'Unknown'}</div>
                      <div className="text-sm text-slate-500">{loan.sector || loan.borrower_sector || '-'}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                  {formatCurrency(loan.amount)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{loan.covenants?.length || 0}</span>
                    {(loan.covenants?.filter(c => c.status !== 'ok').length || 0) > 0 && (
                      <span className="text-xs text-red-600">
                        ({loan.covenants.filter(c => c.status !== 'ok').length} issues)
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge status={loan.status} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <Link
                    to={`/loans/${loan.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View Details
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}